export function createCartSheet({ root, track }) {
  const dialog = root.querySelector('[data-cart-sheet]');
  if (!dialog) return null;

  const title = dialog.querySelector('[data-sheet-title]');
  const tag = dialog.querySelector('[data-sheet-tag]');
  const desc = dialog.querySelector('[data-sheet-desc]');
  const price = dialog.querySelector('[data-sheet-price]');
  const cta = dialog.querySelector('[data-sheet-cta]');
  const carousel = dialog.querySelector('[data-sheet-carousel]');
  const slidesTrack = dialog.querySelector('[data-sheet-track]');
  const studio = dialog.querySelector('[data-sheet-studio]');
  const ambient = dialog.querySelector('[data-sheet-ambient]');
  const ambientSlide = dialog.querySelector('[data-sheet-ambient-slide]');
  const dots = Array.from(dialog.querySelectorAll('[data-sheet-dot]'));
  const mount = dialog.querySelector('[data-sheet-mount]');
  const detail = dialog.querySelector('[data-sheet-detail]');
  const skip = dialog.querySelector('[data-sheet-skip]');

  let current = null;
  let generation = 0;
  let closing = false;
  const stamp = () => { generation += 1; return generation; };
  const isCurrent = (token) => token === generation;

  const isOpen = () => dialog.hasAttribute('open') || dialog.open === true;

  function setImage(img, src, alt) {
    if (!img) return;
    if (src) img.setAttribute('src', src);
    else img.removeAttribute('src');
    img.setAttribute('alt', alt || '');
  }

  const slideWidth = () => slidesTrack.clientWidth || 1;
  const slideCount = () => (carousel.classList.contains('is-single') ? 1 : 2);
  const currentIndex = () => Math.max(0, Math.min(slideCount() - 1, Math.round((slidesTrack.scrollLeft || 0) / slideWidth())));

  function paintDots() {
    const index = currentIndex();
    dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.sheetDot) === index));
    carousel.classList.toggle('is-start', index === 0);
    carousel.classList.toggle('is-end', index >= slideCount() - 1);
  }

  function goTo(index) {
    const target = Math.max(0, Math.min(slideCount() - 1, index));
    if (typeof slidesTrack.scrollTo === 'function') {
      slidesTrack.scrollTo({ left: target * slideWidth(), behavior: 'smooth' });
    } else {
      slidesTrack.scrollLeft = target * slideWidth();
    }
  }

  function resetCarousel() {
    slidesTrack.scrollLeft = 0;
    paintDots();
  }

  function showDialog() {
    if (isOpen()) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  function open(cardCta, { opener } = {}) {
    if (!cardCta) return;
    const data = cardCta.dataset;
    const accordion = cardCta.closest('[data-upsell]');
    const family = accordion ? accordion.dataset.family : '';
    generation += 1;
    current = { group: data.group, addon: data.addon, opener: opener || cardCta, onClose: null };

    title.textContent = data.noticeName || '';
    setTag('');
    desc.textContent = data.sheetDesc || '';
    desc.hidden = !data.sheetDesc;
    price.textContent = data.priceFormatted || '';
    price.hidden = !data.priceFormatted;
    setImage(studio, data.sheetStudio, data.noticeName);
    if (studio && data.sheetStudioW && data.sheetStudioH) {
      studio.setAttribute('width', data.sheetStudioW);
      studio.setAttribute('height', data.sheetStudioH);
    }
    setImage(ambient, data.sheetAmbient, data.sheetAmbientAlt);
    const single = !data.sheetAmbient;
    carousel.classList.toggle('is-single', single);
    if (ambientSlide) ambientSlide.hidden = single;
    carousel.hidden = false;
    if (mount) { mount.hidden = true; mount.replaceChildren(); }
    if (detail) detail.hidden = false;
    if (skip) skip.hidden = false;

    const added = data.action === 'upsell-remove' || !!cardCta.closest('.is-added');
    cta.textContent = added ? (data.sheetCtaAdded || dialog.dataset.sheetRemove || '') : (data.sheetCtaAdd || '');
    cta.hidden = !cta.textContent;
    cta.disabled = false;

    showDialog();
    resetCarousel();
    track('cart_upsell_sheet_open', { sheet: data.addon, family });
  }

  function setTag(text) {
    if (!tag) return;
    tag.textContent = text || '';
    tag.hidden = !text;
  }

  function openCustom({ title: titleText, tag: tagText, opener, mountInto, onClose, sheet, family, group }) {
    generation += 1;
    current = { group: group || '', addon: sheet || '', opener: opener || null, onClose: onClose || null };
    title.textContent = titleText || '';
    setTag(tagText);
    carousel.hidden = true;
    if (detail) detail.hidden = true;
    cta.hidden = true;
    if (skip) skip.hidden = true;
    if (mount) {
      mount.replaceChildren();
      mount.hidden = false;
      if (typeof mountInto === 'function') mountInto(mount);
    }
    showDialog();
    if (sheet) track('cart_upsell_sheet_open', { sheet, family: family || '' });
  }

  function close() {
    if (!isOpen()) return;
    closing = true;
    generation += 1;
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      onClosed();
    }
  }

  function onClosed() {
    const selfClosed = closing;
    closing = false;
    if (isOpen()) return;
    if (!selfClosed) generation += 1;
    const leaving = current;
    current = null;
    if (mount) { mount.replaceChildren(); mount.hidden = true; }
    if (!leaving) return;
    if (typeof leaving.onClose === 'function') leaving.onClose();
    const { opener } = leaving;
    if (opener && root.contains(opener) && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
  }

  function liveCardCta() {
    if (!current || !current.group) return null;
    const accordions = Array.from(root.querySelectorAll('[data-upsell]'));
    const accordion = accordions.find((el) => el.dataset.group === current.group);
    return accordion ? accordion.querySelector(`[data-offer="${current.addon}"] .cart-upsell__cta`) : null;
  }

  function onCta() {
    const button = liveCardCta();
    close();
    if (button) button.click();
  }

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      close();
      return;
    }
    const trigger = event.target.closest('[data-action]');
    if (!trigger || !dialog.contains(trigger)) return;
    switch (trigger.dataset.action) {
      case 'close-sheet':
        event.preventDefault();
        close();
        break;
      case 'sheet-cta':
        event.preventDefault();
        onCta();
        break;
      case 'sheet-prev':
        event.preventDefault();
        goTo(currentIndex() - 1);
        break;
      case 'sheet-next':
        event.preventDefault();
        goTo(currentIndex() + 1);
        break;
      case 'sheet-dot':
        event.preventDefault();
        goTo(Number(trigger.dataset.sheetDot));
        break;
      default:
        break;
    }
  });
  dialog.addEventListener('close', onClosed);
  const drawer = root.getElementById('cart-drawer');
  if (drawer) drawer.addEventListener('close', () => { generation += 1; close(); });
  slidesTrack.addEventListener('scroll', paintDots);

  return { open, openCustom, close, isOpen, mount, stamp, isCurrent };
}

if (typeof document !== 'undefined') {
  const sheet = createCartSheet({
    root: document,
    track: (event, payload) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...payload });
    },
  });
  if (sheet) {
    window.fnCartSheet = sheet;
    document.dispatchEvent(new CustomEvent('fn:cart-sheet-ready'));
  }
}
